import { axios } from "../../lib/axios";

const DEFAULT_TURN_CREDENTIAL_TTL_MS = 4 * 60 * 1_000;
const TURN_CREDENTIAL_REFRESH_SAFETY_MS = 30_000;

export class ClinicalWebRtcBootstrapError extends Error {
  constructor(message, { code = "bootstrap-failed", cause } = {}) {
    super(message);
    this.name = "ClinicalWebRtcBootstrapError";
    this.code = code;
    this.cause = cause;
  }
}

const normalizeTurnServer = (server) => ({
  urls: Array.isArray(server?.Uris) ? server.Uris : server?.Uris,
  username: server?.Username,
  credential: server?.Password,
});

export const getKvsWebRtcBootstrap = async ({
  deviceId,
  organizationId,
  role,
}) => {
  if (!deviceId || !organizationId || !["MASTER", "VIEWER"].includes(role)) {
    throw new ClinicalWebRtcBootstrapError("WebRTC bootstrap parameters are incomplete", {
      code: "invalid-bootstrap-parameters",
    });
  }

  let response;
  try {
    response = await axios.post(
      `device/webrtc?orgId=${encodeURIComponent(organizationId)}`,
      { deviceID: deviceId, userType: role },
    );
  } catch (error) {
    throw new ClinicalWebRtcBootstrapError(
      error?.response?.data?.message || error?.message || "Unable to obtain WebRTC connection information",
      { cause: error },
    );
  }

  const result = response?.result;
  const endpointMap = new Map(
    (result?.signalEndpoint?.ResourceEndpointList || []).map(({ Protocol, ResourceEndpoint }) => (
      [Protocol, ResourceEndpoint]
    )),
  );
  const credentials = result?.stsCredentials;

  if (
    response?.status !== true
    || !result?.channelARN
    || !result?.region
    || result?.role !== role
    || !endpointMap.get("WSS")
    || !credentials?.AccessKeyId
    || !credentials?.SecretAccessKey
    || !credentials?.SessionToken
  ) {
    throw new ClinicalWebRtcBootstrapError(
      response?.message || "The WebRTC bootstrap response is incomplete",
      { code: "invalid-bootstrap-response" },
    );
  }

  const rawTurnServers = Array.isArray(result.iceServers) ? result.iceServers : [];
  const turnServers = rawTurnServers
    .map(normalizeTurnServer)
    .filter(({ urls, username, credential }) => urls && username && credential);
  if (turnServers.length === 0) {
    throw new ClinicalWebRtcBootstrapError(
      "The WebRTC bootstrap response contains no TURN servers",
      { code: "missing-turn-servers" },
    );
  }

  const turnTtlSeconds = rawTurnServers
    .map(({ Ttl }) => Number(Ttl))
    .filter((ttl) => Number.isFinite(ttl) && ttl > 0);
  const turnTtlMs = turnTtlSeconds.length > 0
    ? Math.min(...turnTtlSeconds) * 1_000
    : DEFAULT_TURN_CREDENTIAL_TTL_MS;

  return {
    channelARN: result.channelARN,
    channelEndpoint: endpointMap.get("WSS"),
    region: result.region,
    role: result.role || role,
    credentials: {
      accessKeyId: credentials.AccessKeyId,
      secretAccessKey: credentials.SecretAccessKey,
      sessionToken: credentials.SessionToken,
      expiration: credentials.Expiration,
    },
    turnCredentialsExpireAt: Date.now() + Math.max(
      30_000,
      turnTtlMs - TURN_CREDENTIAL_REFRESH_SAFETY_MS,
    ),
    iceServers: [
      { urls: `stun:stun.kinesisvideo.${result.region}.amazonaws.com:443` },
      ...turnServers,
    ],
  };
};
